import axiosInstance from "../api/axiosInstance";

// Superdoc memory: what the assistant remembers about a clinician and their
// practice. `user` / `user_patient` entries belong to the caller; `clinic`
// entries are shared and editable only when the backend says `can_edit_clinic`.
export type MemoryScope = "clinic" | "user" | "user_patient";
export type MemoryOrigin = "explicit" | "inferred" | "operator";
export type MemoryStatus = "active" | "pending" | "retired";

// An order rule: `when` conditions (all must hold) and `then` effects. The
// backend validates and normalizes; the page only shows the chips it renders.
export interface MemoryRule {
  when: {
    order?: string[];
    order_type?: string;
    dx?: string[];
    location?: string;
    portal?: string;
  };
  then: {
    catalog_item?: { id: string; label: string };
    lab_company?: string;
    file_under_dx?: string;
    replace_labs_with?: { id: string; label: string };
  };
}

export interface MemoryRuleChips {
  when: string[];
  then: string[];
}

export interface MemoryEntryDto {
  id: string;
  scope: MemoryScope;
  category: string;
  category_label: string;
  statement: string;
  rule: MemoryRule | null;
  rule_chips: MemoryRuleChips | null;
  // Whether every EHR item the rule names is matched to an exact catalogue id.
  bound: boolean;
  origin: MemoryOrigin;
  status: MemoryStatus;
  sensitivity: string;
  client_id: number | null;
  group_id: number | null;
  replaces_id: string | null;
  created_at: string;
  valid_from: string | null;
  valid_to: string | null;
  expiry_reason: string;
  retired_at: string | null;
  retired_reason: string;
  undo_until: string | null;
  attributes: Record<string, unknown>;
}

export interface MemoryEntriesResponse {
  entries: MemoryEntryDto[];
  can_edit_clinic: boolean;
  categories: Record<string, string>;
}

export interface EntryResponse {
  entry: MemoryEntryDto;
}

export interface RetireResponse {
  ok: boolean;
  id: string;
}

export interface MemoryListParams {
  scope?: string;
  status?: string;
}

export const CLINIC_MEMORY_CATEGORIES: { value: string; label: string }[] = [
  { value: "orders.rule", label: "Order rule" },
  { value: "practice.fact", label: "Practice fact" },
  { value: "standing.instruction", label: "Standing instruction" },
];

// The order-rule effects an admin can set from the page. `catalog_item` is
// left to chat and the card: it needs the live catalogue to bind.
export const CLINIC_RULE_EFFECTS: { value: keyof MemoryRule["then"]; label: string; placeholder: string }[] = [
  { value: "lab_company", label: "Send labs to", placeholder: "e.g. Quest Diagnostics" },
  { value: "replace_labs_with", label: "Send all labs as the order set", placeholder: "e.g. AI Lab Cascade" },
];

export interface ClinicRuleInput {
  effect: keyof MemoryRule["then"];
  value: string;
  location?: string;
}

export const buildClinicRule = (input: ClinicRuleInput): MemoryRule => {
  const when: MemoryRule["when"] = { order_type: "lab" };
  if (input.location?.trim()) when.location = input.location.trim();
  const then: MemoryRule["then"] = {};
  if (input.effect === "replace_labs_with") then.replace_labs_with = { id: "", label: input.value.trim() };
  else if (input.effect === "lab_company") then.lab_company = input.value.trim();
  return { when, then };
};

const BASE = "/superdoc/memory";

export const memoryService = {
  listEntries: async (params?: MemoryListParams): Promise<MemoryEntriesResponse> => {
    const response = await axiosInstance.get<MemoryEntriesResponse>(`${BASE}/entries/`, { params });
    return response.data;
  },

  editEntry: async (id: string, statement: string): Promise<EntryResponse> => {
    const response = await axiosInstance.patch<EntryResponse>(`${BASE}/entries/${id}/`, { statement });
    return response.data;
  },

  confirmEntry: async (id: string): Promise<EntryResponse> => {
    const response = await axiosInstance.post<EntryResponse>(`${BASE}/entries/${id}/confirm/`);
    return response.data;
  },

  dismissEntry: async (id: string): Promise<RetireResponse> => {
    const response = await axiosInstance.post<RetireResponse>(`${BASE}/entries/${id}/retire/`, {
      reason: "dismissed",
    });
    return response.data;
  },

  // Permanent: the server redacts the entry (statement and rule scrubbed).
  deleteEntry: async (id: string): Promise<RetireResponse> => {
    const response = await axiosInstance.post<RetireResponse>(`${BASE}/entries/${id}/retire/`, {
      reason: "user_deleted",
    });
    return response.data;
  },

  createClinicEntry: async (statement: string, category: string, rule?: MemoryRule): Promise<EntryResponse> => {
    const response = await axiosInstance.post<EntryResponse>(`${BASE}/clinic/`, {
      statement,
      category,
      ...(rule ? { rule } : {}),
    });
    return response.data;
  },
};
